/* Google Drive backup for the local-first Private Vault.
   The vault backup blob (.tpvault.json) is ALREADY encrypted with the user's
   PIN — this module just uploads / lists / downloads the ciphertext to the
   user's own Drive. The backend never touches it.

   Uses Google Identity Services (GIS) token client with `drive.file` scope:
   the app can only see files it created (least-privilege). Loaded from
   https://accounts.google.com/gsi/client (added in public/index.html).

   Contract:
   - initDriveClient({ clientId })         → seed the token client
   - requestDriveToken({ prompt='' })      → resolves to access_token (~1h)
   - uploadEncryptedBackup(token, blob)    → POST /upload/drive/v3/files
   - listVaultBackups(token)               → GET  /drive/v3/files?q=…
   - downloadBackup(token, fileId)         → GET  /drive/v3/files/{id}?alt=media
*/

const APP_FOLDER_NAME = "TravelerPulse Vault Backups";
const MIME_JSON = "application/json";

let _tokenClient = null;
let _clientId = null;

export function driveConfigured() {
  return Boolean(process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID);
}

export function initDriveClient({ clientId } = {}) {
  _clientId = clientId || process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID;
  if (!_clientId) throw new Error("Google Drive backup is not configured — REACT_APP_GOOGLE_DRIVE_CLIENT_ID missing");
  if (!window.google?.accounts?.oauth2) throw new Error("Google Identity Services script hasn't loaded yet");
  _tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: _clientId,
    scope: "https://www.googleapis.com/auth/drive.file",
    callback: () => {}, // overridden per-request
  });
  return _tokenClient;
}

export function requestDriveToken({ prompt = "" } = {}) {
  if (!_tokenClient) initDriveClient();
  return new Promise((resolve, reject) => {
    _tokenClient.callback = (resp) => {
      if (resp.error) reject(new Error(resp.error_description || resp.error));
      else resolve(resp.access_token);
    };
    try { _tokenClient.requestAccessToken({ prompt }); }
    catch (e) { reject(e); }
  });
}

async function _ensureFolder(token) {
  // Look for an existing app folder (drive.file scope only shows files WE created)
  const q = encodeURIComponent(`mimeType='application/vnd.google-apps.folder' and name='${APP_FOLDER_NAME}' and trashed=false`);
  const r = await fetch(`https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`Drive folder lookup failed: ${r.status}`);
  const data = await r.json();
  if (data.files?.[0]) return data.files[0].id;
  // Create it
  const c = await fetch("https://www.googleapis.com/drive/v3/files", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": MIME_JSON },
    body: JSON.stringify({ name: APP_FOLDER_NAME, mimeType: "application/vnd.google-apps.folder" }),
  });
  if (!c.ok) throw new Error(`Drive folder create failed: ${c.status}`);
  const created = await c.json();
  return created.id;
}

export async function uploadEncryptedBackup(token, bundleJsonString) {
  const folderId = await _ensureFolder(token);
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const name = `travelerpulse-vault-${stamp}.tpvault.json`;
  // Multipart upload: metadata + content in one round-trip
  const boundary = "tpv_" + Math.random().toString(36).slice(2);
  const metadata = { name, parents: [folderId], mimeType: MIME_JSON };
  const body =
    `--${boundary}\r\nContent-Type: ${MIME_JSON}; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\nContent-Type: ${MIME_JSON}\r\n\r\n${bundleJsonString}\r\n--${boundary}--`;
  const r = await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,createdTime,size", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": `multipart/related; boundary=${boundary}`,
    },
    body,
  });
  if (!r.ok) throw new Error(`Drive upload failed: ${r.status}`);
  return r.json();
}

export async function listVaultBackups(token) {
  const q = encodeURIComponent(`name contains '.tpvault.json' and trashed=false`);
  const r = await fetch(`https://www.googleapis.com/drive/v3/files?q=${q}&orderBy=createdTime desc&fields=files(id,name,createdTime,size)`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`Drive list failed: ${r.status}`);
  const data = await r.json();
  return data.files || [];
}

export async function downloadBackup(token, fileId) {
  const r = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`Drive download failed: ${r.status}`);
  return r.text();
}
